/* eslint-disable @typescript-eslint/ban-ts-comment */
import Chevron from "../../components/Icons/Chevron";
import { SetStateAction, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import Button from "../../components/Shared/Button";
import { useParams, useNavigate } from "react-router-dom";
import { ChaptersDTO, getChapters } from "../../api/Chapters/GetAllChapters";
import Input from "../../components/Shared/Input";
import { ButtonTypes } from "../../utils/consts";
import ChooseLecture from "../../components/Dashboard/AddQuiz/ChooseLecture";
import TextArea from "../../components/Shared/TextArea";
import Spinner from "../../components/Shared/Spinner";
import MultipleUpload from "../../components/Shared/MultipleUpload";
import { toast } from "react-toastify";
import McqIcon from "../../components/Icons/McqIcon";
import EssayIcon from "../../components/Icons/EssayIcon";
import Upload from "../../components/Shared/Upload";
import ChooseOpenLecture from "../../components/Dashboard/AddQuiz/ChooseOpenLecture";
import ExamIcon from "../../components/Icons/ExamIcon";
import { CreateExam } from "../../api/Quiz/AddExam";
import { CreateEssayQuiz } from "../../api/Quiz/AddEssayQuiz";
import { CreateQuiz } from "../../api/Quiz/AddQuiz";
import { axios } from "../../lib/axios";
import ComboBox from "../../components/Shared/ComboBox";
import { GetAllBankQuestions } from "../../api/QuestionBank/GetAllBankQuestions";
import CBQIcon from "../../components/Icons/CBQIcon";
import CBQBuilder, { CBQBuilderHandle } from "../../components/Dashboard/AddQuiz/CBQBuilder/CBQBuilder";
import { DraftCBQExam, makeDraftExam } from "../../components/Dashboard/AddQuiz/CBQBuilder/types";
import { PublishCBQExam } from "../../api/Quiz/PublishCBQExam";
interface Choice {
  answer: string;
  isCorrect: boolean;
}

interface Question {
  id: number;
  name: string;
  choices: Choice[];
  correctAnswerIndex: number;
  explanation: string;
  figure: string[];
  attachment: string;
}

const AddQuiz = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selected, setSelected] = useState<ChaptersDTO | null>(null);
  const [selectedOpenLecture, setSelectedOpenLecture] = useState<ChaptersDTO | null>(null);
  const [selectedQuizType, setSelectedQuizType] = useState('');
  const [selectedBankQuestions, setSelectedBankQuestions] = useState<{ id: string; name: string }[]>([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [, setErrors] = useState({
    title: '',
    questions: [] as string[],
  });

  const [activeExamTab, setActiveExamTab] = useState('tab1');

  const [mcqQuestions, setMcqQuestions] = useState<Question[]>([
    {
      id: 1,
      name: "",
      choices: [
        { answer: "", isCorrect: true },
        { answer: "", isCorrect: false },
        { answer: "", isCorrect: false },
        { answer: "", isCorrect: false },
      ],
      correctAnswerIndex: 0,
      explanation: "",
      attachment: "",
      figure: [],
    },
  ]);

  const [essayQuestions, setEssayQuestions] = useState<Question[]>([
    {
      id: 1,
      name: "",
      choices: [],
      correctAnswerIndex: 0,
      explanation: "",
      attachment: "",
      figure: [],
    },
  ]);

  const [nextMcqId, setNextMcqId] = useState(2);
  const [nextEssayId, setNextEssayId] = useState(2);

  const validateForm = () => {
    let hasError = false;
    const questionErrors: string[] = [];

    // Validate quiz title
    if (!quizTitle.trim()) {
      setErrors(prev => ({ ...prev, title: "Quiz title is required." }));
      hasError = true;
    } else {
      setErrors(prev => ({ ...prev, title: "" }));
    }

    // Validate questions based on the quiz type
    if (selectedQuizType === 'mcq' || activeExamTab === 'tab2') {
      mcqQuestions.forEach((question, index) => {
        if (!question.name.trim() || question.choices.some(choice => !choice.answer.trim())) {
          questionErrors[index] = "All questions and choices are required.";
          hasError = true;
        } else {
          questionErrors[index] = "";
        }
      });
    } else if (selectedQuizType === 'essay' || activeExamTab === 'tab1') {
      essayQuestions.forEach((question, index) => {
        if (!question.name.trim()) {
          questionErrors[index] = "Question name is required.";
          hasError = true;
        } else {
          questionErrors[index] = "";
        }
      });
    }

    setErrors(prev => ({ ...prev, questions: questionErrors }));
    return !hasError;
  };
  const limit = 9999999999;

  const chapterId = id || "";
  const { data: ChaptersResponse, isLoading: isChaptersLoading } = useQuery({
    queryFn: () => getChapters(chapterId),
    queryKey: ["chapters", chapterId],
    staleTime: 0,
    refetchInterval: 0,
  });
  const { data: Bank } = useQuery({
    queryFn: () => GetAllBankQuestions({
      limit,
    }),
    queryKey: ["bank"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const BankQuestions = Bank?.data?.questions || [];
  const bankOptions = BankQuestions.map((question) => ({
    id: question.id,
    name: question.question,
  }));

  const chapters = (ChaptersResponse?.data as ChaptersDTO[] | undefined) || [];
  const [quizTitle, setQuizTitle] = useState("");
  const [quizDuration, setQuizDuration] = useState("");
  const [cbqDraft, setCbqDraft] = useState<DraftCBQExam>(makeDraftExam());
  // Live-authoring builder persists each action itself; this handle lets submit
  // flush pending debounced patches and read the server-created exam id.
  const cbqBuilderRef = useRef<CBQBuilderHandle>(null);

  const handleAddQuestion = (type: string) => {
    if (type === 'mcq') {
      const newQuestion: Question = {
        id: nextMcqId,
        name: "",
        choices: [
          { answer: "", isCorrect: true },
          { answer: "", isCorrect: false },
          { answer: "", isCorrect: false },
          { answer: "", isCorrect: false },
        ],
        correctAnswerIndex: 0,
        explanation: "",
        attachment: "",
        figure: [],
      };
      setMcqQuestions([...mcqQuestions, newQuestion]);
      setNextMcqId(nextMcqId + 1);
    } else {
      const newQuestion: Question = {
        id: nextEssayId,
        name: "",
        choices: [],
        correctAnswerIndex: 0,
        explanation: "",
        attachment: "",
        figure: [],
      };
      setEssayQuestions([...essayQuestions, newQuestion]);
      setNextEssayId(nextEssayId + 1);
    }
  };

  const handleDeleteQuestion = (questionId: number, type: string) => {
    if (type === 'mcq') {
      setMcqQuestions(mcqQuestions.filter((question) => question.id !== questionId));
    } else {
      setEssayQuestions(essayQuestions.filter((question) => question.id !== questionId));
    }
  };

  const handleQuestionFieldChange = (
    questionIndex: number,
    field: string,
    value: string | boolean,
    choiceIndex?: number,
    type?: string
  ) => {
    if (type === 'mcq' || activeExamTab === 'tab2') {
      setMcqQuestions((prevQuestions) =>
        prevQuestions.map((question, index) => {
          if (index === questionIndex) {
            if (typeof choiceIndex !== "undefined") {
              const updatedChoices = question.choices.map((choice, idx) =>
                idx === choiceIndex ? { ...choice, [field]: value } : choice
              );
              return { ...question, choices: updatedChoices };
            } else {
              return { ...question, [field]: value };
            }
          }
          return question;
        })
      );
    } else {
      setEssayQuestions((prevQuestions) =>
        prevQuestions.map((question, index) => {
          if (index === questionIndex) {
            return { ...question, [field]: value };
          }
          return question;
        })
      );
    }
  };

  const handleCorrectAnswerChange = (
    questionIndex: number,
    selectedChoiceIndex: number,
    type?: string
  ) => {
    if (type === 'mcq' || activeExamTab === 'tab2') {
      setMcqQuestions((prevQuestions) =>
        prevQuestions.map((question, index) => {
          if (index === questionIndex) {
            const updatedChoices = question.choices.map((choice, idx) => ({
              ...choice,
              isCorrect: idx === selectedChoiceIndex,
            }));
            return {
              ...question,
              choices: updatedChoices,
              correctAnswerIndex: selectedChoiceIndex,
            };
          }
          return question;
        })
      );
    }
  };

  const { mutate } = useMutation({
    //@ts-ignore
    mutationFn: selectedQuizType === 'exam' ? CreateExam : selectedQuizType === 'mcq' ? CreateQuiz : CreateEssayQuiz,
    onSuccess: () => {
      setIsSubmitting(true); // Disable the submit button
      toast.success("Quiz Added successfully");
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  // MCQ creation mirrors the host quiz flow (HostQuizs.tsx): generate an id,
  // assign each question, then finalize. Only diffs from host: isHost=false and
  // the finalize body carries lectureId (closed) + OpenLectureId (open).
  const submitMcqHostFlow = async () => {
    try {
      setIsSubmitting(true);

      const genRes = await axios.post("/quiz/generate/id");
      const quizId = genRes.data.id;

      // existing question-bank questions, referenced by id
      for (const bank of selectedBankQuestions) {
        await axios.post(`/quiz/${quizId}/assign-question`, { id: bank.id });
      }

      // authored questions (same body shape as HostQuizs assign-question)
      for (const question of mcqQuestions) {
        await axios.post(`/quiz/${quizId}/assign-question`, {
          question: question.name,
          explanation: question.explanation,
          attachment: question.attachment,
          figure: question.figure,
          choices: question.choices.map((choice) => ({
            answer: choice.answer,
            isCorrect: choice.isCorrect,
          })),
        });
      }

      await axios.patch(
        `/quiz/${quizId}`,
        {
          title: quizTitle,
          lectureId: selected?.lectureId,
          OpenLectureId: selectedOpenLecture?.OpenLectureId,
          duration: String(Number(quizDuration) * 60),
        },
        { params: { isHost: false } }
      );

      toast.success("Quiz Added successfully");
      navigate(-1);
    } catch (error) {
      setIsSubmitting(false);
      toast.error("Failed to add quiz");
    }
  };

  const handleCbqSubmit = async () => {
    const lectureId = selected?.lectureId;
    if (!lectureId) {
      toast.error("No lecture selected");
      return;
    }
    if (!cbqDraft.title.trim()) {
      toast.error("Exam title is required");
      return;
    }
    if (cbqDraft.scenarios.length === 0) {
      toast.error("Add at least one scenario");
      return;
    }
    for (let s = 0; s < cbqDraft.scenarios.length; s++) {
      const sc = cbqDraft.scenarios[s];
      if (!sc.title.trim()) {
        toast.error(`Scenario ${s + 1}: title is required`);
        return;
      }
      if (!sc.pdfS3Key.trim()) {
        toast.error(`Scenario ${s + 1}: please upload a PDF`);
        return;
      }
      if (sc.questions.length === 0) {
        toast.error(`Scenario ${s + 1}: add at least one question`);
        return;
      }
      for (let q = 0; q < sc.questions.length; q++) {
        const qq = sc.questions[q];
        if (!qq.prompt.trim()) {
          toast.error(`Scenario ${s + 1} · Q${q + 1}: prompt is required`);
          return;
        }
        if (!(qq.points > 0)) {
          toast.error(`Scenario ${s + 1} · Q${q + 1}: points must be positive`);
          return;
        }
      }
    }

    setIsSubmitting(true);
    // Everything was persisted live as the admin authored it. Flush any pending
    // debounced text patches, then publish the already-created draft exam.
    await cbqBuilderRef.current?.flushPending();
    const examId = cbqBuilderRef.current?.getExamId() ?? cbqDraft.examId;
    if (!examId) {
      toast.error("CBQ exam was not created — add a scenario first");
      setIsSubmitting(false);
      return;
    }
    const pub = await PublishCBQExam(examId);
    if (pub.error) {
      // Publish validation errors may come back as an array of lines.
      const m = pub.error.message as unknown;
      toast.error(
        Array.isArray(m)
          ? (m as string[]).join("\n")
          : (m as string) || "CBQ exam saved but could not be published"
      );
      setIsSubmitting(false);
      return;
    }
    toast.success("CBQ exam created and published");
    navigate(-1);
  };

  // Save-as-draft: the builder already persists every action live, so this just
  // force-flushes the last debounced edits and confirms — it does NOT publish,
  // and the admin stays in the builder. Publishing is the "Create CBQ Exam"
  // button, which flips the exam status to published.
  const handleSaveCbqDraft = async () => {
    if (selectedQuizType !== "cbq") return;
    setIsSavingDraft(true);
    await cbqBuilderRef.current?.flushPending();
    const examId = cbqBuilderRef.current?.getExamId() ?? cbqDraft.examId;
    setIsSavingDraft(false);
    if (!examId) {
      toast.error("Nothing to save yet — add a scenario first");
      return;
    }
    toast.success("Draft saved");
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (!selected) {
      console.error("No lecture selected");
      return;
    }
    if (selectedQuizType === 'cbq') {
      handleCbqSubmit();
      return;
    }
    const selectedBankQuestionIds = selectedBankQuestions.map((question) => ({ id: question.id }));

    if (validateForm()) {
      if (selectedQuizType === 'mcq') {
        submitMcqHostFlow();
        return;
      }
      let payload;
      const durationInSeconds = String(Number(quizDuration) * 60);
      if (selectedQuizType === 'exam') {
        payload = {
          title: quizTitle,
          duration: durationInSeconds,
          lectureId: selected?.lectureId,
          Quiz: {
            OpenLectureId: selectedOpenLecture?.OpenLectureId,
            title: quizTitle,
            questions: mcqQuestions.map(question => ({
              question: question.name,
              explanation: question.explanation,
              choices: question.choices.map(choice => ({
                answer: choice.answer,
                isCorrect: choice.isCorrect,
              })),
              attachment: question.attachment,
              figure: question.figure,
            })),
          },
          QuizEssay: {
            title: quizTitle,
            OpenLectureId: selectedOpenLecture?.OpenLectureId,
            QuestionEssay: essayQuestions.map(question => ({
              question: question.name,
              explanation: question.explanation,
              attachment: question.attachment,
              figure: question.figure,
            })),
          },
        };
      } else if (selectedQuizType === 'mcq' || activeExamTab === 'tab2') {
        payload = {
          title: quizTitle,
          duration: durationInSeconds,
          questions: [
            ...selectedBankQuestionIds,
            ...mcqQuestions.map(question => ({
              question: question.name,
              explanation: question.explanation,
              choices: question.choices.map(choice => ({
                answer: choice.answer,
                isCorrect: choice.isCorrect,
              })),
              attachment: question.attachment,
              figure: question.figure,
            })),
          ],
          lectureId: selected?.lectureId,
          OpenLectureId: selectedOpenLecture?.OpenLectureId,
          chapterId: selected?.chapterId,
        };
      } else if (selectedQuizType === 'essay' || activeExamTab === 'tab1') {
        payload = {
          title: quizTitle,
          duration: durationInSeconds,
          QuestionEssay: essayQuestions.map(question => ({
            question: question.name,
            explanation: question.explanation,
            attachment: question.attachment,
            figure: question.figure,
          })),
          lectureId: selected?.lectureId,
          OpenLectureId: selectedOpenLecture?.OpenLectureId,
          chapterId: selected?.chapterId,
        };
      }

      setIsSubmitting(true);
      //@ts-expect-error
      mutate(payload);
    }
  };

  const handleFileUploaded = (url: string, questionId: number, type: string) => {
    if (type === 'mcq' || activeExamTab === 'tab2') {
      setMcqQuestions((prevQuestions) => {
        const updatedQuestions = prevQuestions.map((question) =>
          question.id === questionId
            ? { ...question, figure: [...question.figure, url] }
            : question
        );
        return updatedQuestions;
      });
    } else {
      setEssayQuestions((prevQuestions) => {
        const updatedQuestions = prevQuestions.map((question) =>
          question.id === questionId
            ? { ...question, figure: [...question.figure, url] }
            : question
        );
        return updatedQuestions;
      });
    }
  };

  const handleAttachmentUpload = (url: string, questionId: number, type: string) => {
    if (type === 'mcq' || activeExamTab === 'tab2') {
      setMcqQuestions((prevQuestions) => {
        const updatedQuestions = prevQuestions.map((question) =>
          question.id === questionId
            ? { ...question, attachment: url }
            : question
        );
        return updatedQuestions;
      });
    } else {
      setEssayQuestions((prevQuestions) => {
        const updatedQuestions = prevQuestions.map((question) =>
          question.id === questionId
            ? { ...question, attachment: url }
            : question
        );
        return updatedQuestions;
      });
    }
  };

  const handleSelectQuizType = (type: SetStateAction<string>) => {
    setSelectedQuizType(type);
  };

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-6">
        Create new quiz
      </h1>
      <form
        onSubmit={handleSubmit}
        className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] space-y-5"
      >
        <div className="flex gap-5 items-center mb-9 mt-4 justify-center">
          <div className={`flex items-center gap-2.5 `}>
            <span
              className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 1 ? "text-white bg-[#4379EE]" : "text-lightGray"
                }`}
            >
              1
            </span>
            <h1 className="font-semibold">Choose Quiz Type</h1>
          </div>
          <Chevron height={20} width={20} />
          <div className={`flex items-center gap-2.5`}>
            <span
              className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 2 ? "text-white bg-[#4379EE]" : "text-lightGray"
                }`}
            >
              2
            </span>
            <h1 className="font-semibold">Choose Lecture</h1>
          </div>
          {selectedQuizType !== 'cbq' && (
            <>
          <Chevron height={20} width={20} />

          <div className={`flex items-center gap-2.5`}>
            <span
              className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 3 ? "text-white bg-[#4379EE]" : "text-lightGray"
                }`}
            >
              3
            </span>
            <h1 className="font-semibold">Closed Lecture</h1>
          </div>
            </>
          )}
          <Chevron height={20} width={20} />

          <div className={`flex items-center gap-2.5 `}>
            <span
              className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 4 ? "text-white bg-[#4379EE]" : "text-lightGray"
                }`}
            >
              {selectedQuizType === 'cbq' ? 3 : 4}
            </span>
            <h1 className="font-semibold">Questions</h1>
          </div>
        </div>
        {currentStep === 1 && (
          <div className="h-[60vh] flex flex-col items-center ">
            <h1 className="text-2xl font-bold mb-2.5 text-center">Choose Quiz Type</h1>
            <div className="flex items-center justify-center gap-20 mt-10">
              <div className="flex flex-col items-center gap-2.5">
                <button onClick={() => handleSelectQuizType('mcq')}
                  className={`w-20 h-20 border-black/15 border-2 rounded-2xl text-primary  hover:border-primary transition-all duration-300 flex items-center justify-center ${selectedQuizType === "mcq" ? 'bg-primary text-white' : ''} `}>
                  <McqIcon />
                </button>
                <span className={`text-sm font-semibold ${selectedQuizType === "mcq" ? 'text-primary' : 'text-lightGray'}`}>MCQ</span>
              </div>
              <div className="flex flex-col items-center gap-2.5">
                <button onClick={() => handleSelectQuizType('essay')}
                  className={`w-20 h-20 border-black/15 border-2 rounded-2xl text-primary  hover:border-primary transition-all duration-300 flex items-center justify-center ${selectedQuizType === "essay" ? 'bg-primary text-white' : ''} `}>
                  <EssayIcon />
                </button>
                <span className={`text-sm font-semibold ${selectedQuizType === "essay" ? 'text-primary' : 'text-lightGray'}`}>Essay</span>
              </div>
              <div className="flex flex-col items-center gap-2.5">
                <button onClick={() => handleSelectQuizType('exam')}
                  className={`w-20 h-20 border-black/15 border-2 rounded-2xl text-primary  hover:border-primary transition-all duration-300 flex items-center justify-center ${selectedQuizType === "exam" ? 'bg-primary text-white' : ''} `}>
                  <ExamIcon />
                </button>
                <span className={`text-sm font-semibold ${selectedQuizType === "exam" ? 'text-primary' : 'text-lightGray'}`}>Exam</span>
              </div>
              <div className="flex flex-col items-center gap-2.5">
                <button type="button" onClick={() => handleSelectQuizType('cbq')}
                  className={`w-20 h-20 border-black/15 border-2 rounded-2xl text-primary  hover:border-primary transition-all duration-300 flex items-center justify-center ${selectedQuizType === "cbq" ? 'bg-primary text-white' : ''} `}>
                  <CBQIcon />
                </button>
                <span className={`text-sm font-semibold ${selectedQuizType === "cbq" ? 'text-primary' : 'text-lightGray'}`}>CBQ</span>
              </div>
            </div>
            <div className="mt-auto ml-auto">
              <Button
                onClick={() => setCurrentStep(2)}
                type={ButtonTypes.BUTTON}
                disabled={selectedQuizType === ''}
                className={`mt-8 w-[160px] !h-11 !text-base font-bold ${selectedQuizType === '' ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                variant="primary"
              >
                Proceed
              </Button>
            </div>

          </div>
        )}
        {currentStep === 2 && (
          <>
            {isChaptersLoading ? (
              <div className="min-h-[50vh] flex justify-center items-center">
                <Spinner />
              </div>
            ) : (
              <ChooseLecture
                message="Select a lesson to make quizzes of it"
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                selected={selected}
                setSelected={setSelected}
                data={chapters}
                nextStep={selectedQuizType === 'cbq' ? 4 : 3}
              />
            )}
          </>
        )}
        {currentStep === 3 && (
          <>
            {isChaptersLoading ? (
              <div className="min-h-[50vh] flex justify-center items-center">
                <Spinner />
              </div>
            ) : (
              <ChooseOpenLecture
                message="Select a lesson that closes before successing the quiz"
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                selected={selectedOpenLecture}
                setSelected={setSelectedOpenLecture}
                data={chapters}
              />
            )}
          </>
        )}
        {currentStep === 4 && (
          <>
            {selectedQuizType === 'cbq' ? (
              <CBQBuilder
                ref={cbqBuilderRef}
                value={cbqDraft}
                onChange={setCbqDraft}
                lectureId={selected?.lectureId}
                mcqQuizzes={
                  chapters
                    .flatMap((c) => c.lectures ?? [])
                    .find((l) => l.id === selected?.lectureId)?.quizizz ?? []
                }
              />
            ) : selectedQuizType === 'exam' ? (
              <div className="flex flex-col items-center justify-center">
                <div className="flex justify-center space-x-4 mb-4">
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg ${activeExamTab === 'tab1' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-black'}`}
                    onClick={() => setActiveExamTab('tab1')}
                  >
                    Essay
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg ${activeExamTab === 'tab2' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-black'}`}
                    onClick={() => setActiveExamTab('tab2')}
                  >
                    MCQ
                  </button>
                </div>
                {activeExamTab === 'tab1' ? (
                  <div className="w-full max-w-[544px] mx-auto">
                    <Input
                      className="text-lg font-semibold mb-10"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                      title="Quiz Title"
                      placeholder=""
                      value={quizTitle}
                      name="title"
                      onChange={(e) => setQuizTitle(e.target.value)}
                    />
                    <Input
                      className="text-lg font-semibold mb-10"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                      title="Quiz Duration (in minutes)"
                      placeholder=""
                      value={quizDuration}
                      name="duration"
                      onChange={(e) => setQuizDuration(e.target.value)}
                    />

                    <div>
                      <h1 className="text-2xl font-bold mb-2.5">Questions</h1>
                      <p className="text-lightGray font-medium">
                        Nice text goes here to explain the question creation part like
                        never before
                      </p>
                    </div>
                    <div>
                      {essayQuestions.map((question, questionIndex) => (
                        <div key={question.id}>
                          <div className="mt-9">
                            <Input
                              className="text-lg font-semibold"
                              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                              title="Question name"
                              placeholder=""
                              name="name"
                              value={question.name}
                              onChange={(e) =>
                                handleQuestionFieldChange(
                                  questionIndex,
                                  "name",
                                  e.target.value,
                                  undefined,
                                  'essay'
                                )
                              }
                              type="text"
                            />
                            <TextArea
                              className="text-lg font-semibold mb-10"
                              inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                              title="Explanation (Optional)"
                              placeholder=""
                              name="explanation"
                              value={question.explanation}
                              onChange={(e) =>
                                handleQuestionFieldChange(
                                  questionIndex,
                                  "explanation",
                                  e.target.value,
                                  undefined,
                                  'essay'
                                )
                              }
                            />
                            <MultipleUpload
                              folder="courses_images"
                              onFileUploaded={(url) =>
                                handleFileUploaded(url, question.id, 'essay')
                              }
                              questionId={question.id}
                            >
                              <Input
                                upload={true}
                                className="text-lg font-semibold mb-10"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                placeholder=""
                                title="Figures"
                                name="img_url"
                                type="text"
                              />
                            </MultipleUpload>
                            <Upload
                              acceptedFiles={{ "pdf": [] }}
                              folder="quiz_resources"
                              onFileUploaded={(url) => handleAttachmentUpload(url, question.id, 'essay')}
                            >
                              <Input
                                upload={true}
                                className="text-lg font-semibold"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                placeholder=""
                                title="Attachments"
                                name="attachment"
                                type="text"
                              />
                            </Upload>

                          </div>
                          <div className="flex justify-end gap-5 mt-10">
                            <Button
                              onClick={() => handleDeleteQuestion(question.id, 'essay')}
                              className={`before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-40 !border-primary !border-2 text-primary ${essayQuestions.length === 1
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                                }`}
                              disabled={essayQuestions.length === 1}
                              type={ButtonTypes.BUTTON}
                              variant="transparent"
                            >
                              Delete
                            </Button>
                            <Button
                              onClick={() => handleAddQuestion('essay')}
                              type={ButtonTypes.BUTTON}
                              variant="primary"
                              className="!h-12 w-40 !text-base !font-bold "
                            >
                              Add New Question
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="w-full max-w-[544px] mx-auto">
                    <Input
                      className="text-lg font-semibold mb-10"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                      title="Quiz Title"
                      placeholder=""
                      value={quizTitle}
                      name="title"
                      onChange={(e) => setQuizTitle(e.target.value)}
                    />
                    <Input
                      className="text-lg font-semibold mb-10"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                      title="Quiz Duration (in minutes)"
                      placeholder=""
                      value={quizDuration}
                      name="duration"
                      onChange={(e) => setQuizDuration(e.target.value)}
                    />

                    <div>
                      <h1 className="text-2xl font-bold mb-2.5">Questions</h1>
                      <p className="text-lightGray font-medium">
                        Nice text goes here to explain the question creation part like
                        never before
                      </p>
                    </div>
                    <div>
                      {mcqQuestions.map((question, questionIndex) => (
                        <div key={question.id}>
                          <div className="mt-9">
                            <Input
                              className="text-lg font-semibold"
                              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                              title="Question name"
                              placeholder=""
                              name="name"
                              value={question.name}
                              onChange={(e) =>
                                handleQuestionFieldChange(
                                  questionIndex,
                                  "name",
                                  e.target.value,
                                  undefined,
                                  'mcq'
                                )
                              }
                              type="text"
                            />
                            <p className="text-lightGray font-medium mb-4">
                              Please mark the correct answer among them
                            </p>
                            <div className="grid-cols-2 grid gap-x-6 gap-y-4">
                              {question.choices.map((choice, choiceIndex) => (
                                <div key={`choice-${choiceIndex}`}>
                                  <div className="flex items-center justify-between">
                                    <label
                                      htmlFor={`choice-radio-${questionIndex}-${choiceIndex}`}
                                      className="text-lg font-semibold"
                                    >
                                      {`Choice ${choiceIndex + 1}`}
                                    </label>
                                    <input
                                      id={`choice-radio-${questionIndex}-${choiceIndex}`}
                                      type="radio"
                                      value={choiceIndex}
                                      name={`question-${question.id}-choices`}
                                      checked={
                                        choiceIndex === question.correctAnswerIndex
                                      }
                                      onChange={() =>
                                        handleCorrectAnswerChange(
                                          questionIndex,
                                          choiceIndex,
                                          'mcq'
                                        )
                                      }
                                      className="w-5 h-5 text-primary bg-gray-100 border-black/30 border-2 focus:ring-0"
                                    />
                                  </div>
                                  <Input
                                    className="text-lg font-semibold"
                                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                                    placeholder=""
                                    name={`choice-${choiceIndex}`}
                                    value={choice.answer}
                                    onChange={(e) =>
                                      handleQuestionFieldChange(
                                        questionIndex,
                                        "answer",
                                        e.target.value,
                                        choiceIndex,
                                        'mcq'
                                      )
                                    }
                                    type="text"
                                  />
                                </div>
                              ))}
                            </div>
                            <TextArea
                              className="text-lg font-semibold mb-10"
                              inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                              title="Explanation (Optional)"
                              placeholder=""
                              name="explanation"
                              value={question.explanation}
                              onChange={(e) =>
                                handleQuestionFieldChange(
                                  questionIndex,
                                  "explanation",
                                  e.target.value,
                                  undefined,
                                  'mcq'
                                )
                              }
                            />
                            <MultipleUpload
                              folder="courses_images"
                              onFileUploaded={(url) =>
                                handleFileUploaded(url, question.id, 'mcq')
                              }
                              questionId={question.id}
                            >
                              <Input
                                upload={true}
                                className="text-lg font-semibold mb-10"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                placeholder=""
                                title="Figures"
                                name="img_url"
                                type="text"
                              />
                            </MultipleUpload>
                            <Upload
                              acceptedFiles={{ "pdf": [] }}
                              folder="quiz_resources"
                              onFileUploaded={(url) => handleAttachmentUpload(url, question.id, 'mcq')}

                            >
                              <Input
                                upload={true}
                                className="text-lg font-semibold"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                placeholder=""
                                title="Attachments"
                                name="attachment"
                                type="text"
                              />
                            </Upload>

                          </div>
                          <div className="flex justify-end gap-5 mt-10">
                            <Button
                              onClick={() => handleDeleteQuestion(question.id, 'mcq')}
                              className={`before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-40 !border-primary !border-2 text-primary ${mcqQuestions.length === 1
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                                }`}
                              disabled={mcqQuestions.length === 1}
                              type={ButtonTypes.BUTTON}
                              variant="transparent"
                            >
                              Delete
                            </Button>
                            <Button
                              onClick={() => handleAddQuestion('mcq')}
                              type={ButtonTypes.BUTTON}
                              variant="primary"
                              className="!h-12 w-40 !text-base !font-bold "
                            >
                              Add New Question
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full max-w-[544px] mx-auto">
                <Input
                  className="text-lg font-semibold mb-10"
                  inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                  title="Quiz Title"
                  placeholder=""
                  value={quizTitle}
                  name="title"
                  onChange={(e) => setQuizTitle(e.target.value)}
                />
                                    <Input
                      className="text-lg font-semibold mb-10"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                      title="Quiz Duration (in minutes)"
                      placeholder=""
                      value={quizDuration}
                      name="duration"
                      onChange={(e) => setQuizDuration(e.target.value)}
                    />

                {selectedQuizType === 'mcq' && (
                  <div>
                    <h1 className="text-2xl font-bold mb-2.5">Add from Questions Bank</h1>
                    <ComboBox
                      bankQuestions={bankOptions}
                      selectedBankQuestions={selectedBankQuestions}
                      setSelectedBankQuestions={setSelectedBankQuestions}
                    />
                  </div>

                )}
                <div>
                  <h1 className="text-2xl font-bold mb-2.5">Questions</h1>
                  <p className="text-lightGray font-medium">
                    Nice text goes here to explain the question creation part like
                    never before
                  </p>
                </div>
                <div>
                  {selectedQuizType === 'mcq' && mcqQuestions.map((question, questionIndex) => (
                    <div key={question.id}>
                      <div className="mt-9">
                        <Input
                          className="text-lg font-semibold"
                          inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                          title="Question name"
                          placeholder=""
                          name="name"
                          value={question.name}
                          onChange={(e) =>
                            handleQuestionFieldChange(
                              questionIndex,
                              "name",
                              e.target.value,
                              undefined,
                              'mcq'
                            )
                          }
                          type="text"
                        />
                        <p className="text-lightGray font-medium mb-4">
                          Please mark the correct answer among them
                        </p>
                        <div className="grid-cols-2 grid gap-x-6 gap-y-4">
                          {question.choices.map((choice, choiceIndex) => (
                            <div key={`choice-${choiceIndex}`}>
                              <div className="flex items-center justify-between">
                                <label
                                  htmlFor={`choice-radio-${questionIndex}-${choiceIndex}`}
                                  className="text-lg font-semibold"
                                >
                                  {`Choice ${choiceIndex + 1}`}
                                </label>
                                <input
                                  id={`choice-radio-${questionIndex}-${choiceIndex}`}
                                  type="radio"
                                  value={choiceIndex}
                                  name={`question-${question.id}-choices`}
                                  checked={
                                    choiceIndex === question.correctAnswerIndex
                                  }
                                  onChange={() =>
                                    handleCorrectAnswerChange(
                                      questionIndex,
                                      choiceIndex,
                                      'mcq'
                                    )
                                  }
                                  className="w-5 h-5 text-primary bg-gray-100 border-black/30 border-2 focus:ring-0"
                                />
                              </div>
                              <Input
                                className="text-lg font-semibold"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                                placeholder=""
                                name={`choice-${choiceIndex}`}
                                value={choice.answer}
                                onChange={(e) =>
                                  handleQuestionFieldChange(
                                    questionIndex,
                                    "answer",
                                    e.target.value,
                                    choiceIndex,
                                    'mcq'
                                  )
                                }
                                type="text"
                              />
                            </div>
                          ))}
                        </div>
                        <TextArea
                          className="text-lg font-semibold mb-10"
                          inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                          title="Explanation (Optional)"
                          placeholder=""
                          name="explanation"
                          value={question.explanation}
                          onChange={(e) =>
                            handleQuestionFieldChange(
                              questionIndex,
                              "explanation",
                              e.target.value,
                              undefined,
                              'mcq'
                            )
                          }
                        />
                        <MultipleUpload
                          folder="courses_images"
                          onFileUploaded={(url) =>
                            handleFileUploaded(url, question.id, 'mcq')
                          }
                          questionId={question.id}
                        >
                          <Input
                            upload={true}
                            className="text-lg font-semibold mb-10"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            placeholder=""
                            title="Figures"
                            name="img_url"
                            type="text"
                          />
                        </MultipleUpload>
                        <Upload
                          acceptedFiles={{ "pdf": [] }}
                          folder="quiz_resources"
                          onFileUploaded={(url) => handleAttachmentUpload(url, question.id, 'mcq')}

                        >
                          <Input
                            upload={true}
                            className="text-lg font-semibold"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            placeholder=""
                            title="Attachments"
                            name="attachment"
                            type="text"
                          />
                        </Upload>

                      </div>
                      <div className="flex justify-end gap-5 mt-10">
                        <Button
                          onClick={() => handleDeleteQuestion(question.id, 'mcq')}
                          className={`before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-40 !border-primary !border-2 text-primary `}
                          type={ButtonTypes.BUTTON}
                          variant="transparent"
                        >
                          Delete
                        </Button>
                        <Button
                          onClick={() => handleAddQuestion('mcq')}
                          type={ButtonTypes.BUTTON}
                          variant="primary"
                          className="!h-12 w-40 !text-base !font-bold "
                        >
                          Add New Question
                        </Button>
                      </div>
                    </div>
                  ))}
                  {selectedQuizType === 'essay' && essayQuestions.map((question, questionIndex) => (
                    <div key={question.id}>
                      <div className="mt-9">
                        <Input
                          className="text-lg font-semibold"
                          inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                          title="Question name"
                          placeholder=""
                          name="name"
                          value={question.name}
                          onChange={(e) =>
                            handleQuestionFieldChange(
                              questionIndex,
                              "name",
                              e.target.value,
                              undefined,
                              'essay'
                            )
                          }
                          type="text"
                        />
                        <TextArea
                          className="text-lg font-semibold mb-10"
                          inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                          title="Explanation (Optional)"
                          placeholder=""
                          name="explanation"
                          value={question.explanation}
                          onChange={(e) =>
                            handleQuestionFieldChange(
                              questionIndex,
                              "explanation",
                              e.target.value,
                              undefined,
                              'essay'
                            )
                          }
                        />
                        <MultipleUpload
                          folder="courses_images"
                          onFileUploaded={(url) =>
                            handleFileUploaded(url, question.id, 'essay')
                          }
                          questionId={question.id}
                        >
                          <Input
                            upload={true}
                            className="text-lg font-semibold mb-10"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            placeholder=""
                            title="Figures"
                            name="img_url"
                            type="text"
                          />
                        </MultipleUpload>
                        <Upload
                          acceptedFiles={{ "pdf": [] }}
                          folder="quiz_resources"
                          onFileUploaded={(url) => handleAttachmentUpload(url, question.id, 'essay')}

                        >
                          <Input
                            upload={true}
                            className="text-lg font-semibold"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            placeholder=""
                            title="Attachments"
                            name="attachment"
                            type="text"
                          />
                        </Upload>

                      </div>
                      <div className="flex justify-end gap-5 mt-10">
                        <Button
                          onClick={() => handleDeleteQuestion(question.id, 'essay')}
                          className={`before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-40 !border-primary !border-2 text-primary ${essayQuestions.length === 1
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                            }`}
                          disabled={essayQuestions.length === 1}
                          type={ButtonTypes.BUTTON}
                          variant="transparent"
                        >
                          Delete
                        </Button>
                        <Button
                          onClick={() => handleAddQuestion('essay')}
                          type={ButtonTypes.BUTTON}
                          variant="primary"
                          className="!h-12 w-40 !text-base !font-bold "
                        >
                          Add New Question
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-between w-full">
              <Button
                disabled={!selected}
                onClick={() => setCurrentStep(2)}
                className={`mt-8 !bg-white border-black/20 border !text-burntBlack w-[160px] !h-11 !text-base font-bold ${!selected ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                variant="primary"
              >
                Cancel
              </Button>
              <div className="flex gap-3">
                {selectedQuizType === 'cbq' && (
                  <Button
                    type={ButtonTypes.BUTTON}
                    onClick={handleSaveCbqDraft}
                    disabled={isSavingDraft || isSubmitting}
                    className={`mt-8 !bg-white border-black/20 border !text-burntBlack w-[160px] !h-11 !text-base font-bold ${!selected ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    variant="primary"
                  >
                    {isSavingDraft ? 'Saving...' : 'Save as Draft'}
                  </Button>
                )}
                <Button
                  type={ButtonTypes.SUBMIT}
                  disabled={isSubmitting}
                  className={`mt-8 w-[160px] !h-11 !text-base font-bold ${!selected ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  variant="primary"
                >
                  {selectedQuizType === 'cbq' ? 'Create CBQ Exam' : 'Send Replies'}
                </Button>
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default AddQuiz;
